import { LEAD_SCREENSHOT_MAX_BYTES, LEAD_SCREENSHOT_NOTE_MAX } from '@utazzo/shared';
import { cn, UzButton, UzConfirmDialog, UzSpinner, UzTextarea } from '@utazzo/ui';
import { ImageIcon, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  useDeleteLeadScreenshot,
  useLeadScreenshots,
  useLeadScreenshotUrl,
  useUploadLeadScreenshots,
} from '../use-lead-screenshots';

import type { OpsActionableKind, LeadScreenshot, LeadScreenshotItem } from '@utazzo/shared';

import { KIND_ORDER } from '@/features/ops-cases/components/OpsBookingSections';
import { actionableKindOf, KIND_META } from '@/features/ops-cases/item-kind';

/**
 * Screenshots tab on the lead detail page (long-haul leads only, after Call
 * Analysis). Same rows as the ops booking card - every item of the converted
 * itinerary, grouped by kind - but by name only (a transfer by its route),
 * and each row takes one or more screenshots from the seller, with an
 * optional note that ops sees next to them on the converted lead.
 */

const ACCEPT = 'image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp';

const Thumb = ({
  leadId,
  shot,
  onDelete,
}: {
  leadId: string;
  shot: LeadScreenshot;
  onDelete: () => void;
}): React.JSX.Element => {
  const { data } = useLeadScreenshotUrl(leadId, shot.id);
  return (
    <div className="group relative">
      <a
        href={data?.url}
        target="_blank"
        rel="noopener noreferrer"
        title={[shot.fileName, shot.uploadedByName, shot.note].filter(Boolean).join(' · ')}
        className="flex h-14 w-20 items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-100"
      >
        {data?.url ? (
          <img src={data.url} alt={shot.fileName} className="h-full w-full object-cover" />
        ) : (
          <ImageIcon className="h-4 w-4 text-zinc-400" />
        )}
      </a>
      {shot.canDelete ? (
        <button
          type="button"
          onClick={onDelete}
          title="Remove"
          className="absolute -top-1.5 -right-1.5 hidden h-5 w-5 items-center justify-center rounded-full bg-rose-600 text-white shadow group-hover:flex"
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
      {shot.note ? (
        <div className="mt-0.5 w-20 truncate text-[11px] text-zinc-500" title={shot.note}>
          {shot.note}
        </div>
      ) : null}
    </div>
  );
};

const ItemRow = ({
  leadId,
  serial,
  item,
  shots,
  canUpload,
  onDelete,
}: {
  leadId: string;
  serial: number;
  item: LeadScreenshotItem;
  shots: LeadScreenshot[];
  canUpload: boolean;
  onDelete: (shot: LeadScreenshot) => void;
}): React.JSX.Element => {
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useUploadLeadScreenshots(leadId);
  // Files chosen but not sent yet: the seller can add a note first.
  const [pending, setPending] = useState<File[] | null>(null);
  const [note, setNote] = useState('');

  const pick = (list: FileList | null): void => {
    const files = Array.from(list ?? []);
    if (!files.length) return;
    const tooBig = files.find((f) => f.size > LEAD_SCREENSHOT_MAX_BYTES);
    if (tooBig) {
      toast.error(`${tooBig.name} is too large (max 8MB)`);
      return;
    }
    setPending(files);
  };

  const cancel = (): void => {
    setPending(null);
    setNote('');
  };

  const send = (): void => {
    if (!pending) return;
    upload.mutate({ componentId: item.componentId, files: pending, note }, { onSuccess: cancel });
  };

  return (
    <tr className="border-b border-zinc-100 align-top last:border-0">
      <td className="w-10 px-4 py-3 text-zinc-500 tabular-nums">{serial}</td>
      <td className="px-4 py-3 font-medium text-zinc-900">{item.name}</td>
      <td className="px-4 py-3">
        {shots.length ? (
          <div className="flex flex-wrap gap-2">
            {shots.map((s) => (
              <Thumb key={s.id} leadId={leadId} shot={s} onDelete={() => onDelete(s)} />
            ))}
          </div>
        ) : (
          <span className="text-xs text-zinc-400">No screenshots</span>
        )}
        {pending ? (
          <div className="mt-2 max-w-md rounded-lg border border-zinc-200 bg-zinc-50 p-2.5">
            <div
              className="truncate text-xs text-zinc-600"
              title={pending.map((f) => f.name).join(', ')}
            >
              {pending.length === 1
                ? pending[0]?.name
                : `${pending.length} files: ${pending.map((f) => f.name).join(', ')}`}
            </div>
            <UzTextarea
              containerClassName="mt-2"
              label="Note for ops (optional)"
              rows={2}
              maxLength={LEAD_SCREENSHOT_NOTE_MAX}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={upload.isPending}
            />
            <div className="mt-2 flex justify-end gap-2">
              <UzButton size="sm" variant="ghost" onClick={cancel} disabled={upload.isPending}>
                Cancel
              </UzButton>
              <UzButton size="sm" loading={upload.isPending} onClick={send}>
                <Upload className="mr-1.5 h-3.5 w-3.5" />
                Upload{pending.length > 1 ? ` ${pending.length}` : ''}
              </UzButton>
            </div>
          </div>
        ) : null}
      </td>
      <td className="w-32 px-4 py-3 text-right">
        {canUpload ? (
          <>
            <UzButton
              size="sm"
              variant="outline"
              disabled={pending !== null || upload.isPending}
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="mr-1.5 h-3.5 w-3.5" /> Upload
            </UzButton>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              multiple
              className="sr-only"
              onChange={(e) => {
                pick(e.target.files);
                e.target.value = '';
              }}
            />
          </>
        ) : null}
      </td>
    </tr>
  );
};

const KindSection = ({
  kind,
  items,
  startIndex,
  shotsByItem,
  leadId,
  canUpload,
  onDelete,
}: {
  kind: OpsActionableKind;
  items: LeadScreenshotItem[];
  startIndex: number;
  shotsByItem: Map<string, LeadScreenshot[]>;
  leadId: string;
  canUpload: boolean;
  onDelete: (shot: LeadScreenshot) => void;
}): React.JSX.Element => {
  const meta = KIND_META[kind];
  const Icon = meta.icon;
  return (
    <section className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
      <header className="flex items-center gap-3 border-b border-zinc-100 bg-zinc-50/60 px-4 py-3">
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full ring-1',
            meta.tone,
          )}
          aria-hidden
        >
          <Icon className="h-4 w-4" />
        </span>
        <h3 className="text-base font-semibold tracking-tight text-zinc-900">
          {meta.plural}
          <span className="ml-1.5 font-medium text-zinc-500 tabular-nums">({items.length})</span>
        </h3>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100 bg-zinc-50/50 text-left">
              <th className="w-10 px-4 py-2.5 text-xs font-medium tracking-wide text-zinc-500 uppercase">
                #
              </th>
              <th className="px-4 py-2.5 text-xs font-medium tracking-wide text-zinc-500 uppercase">
                {kind === 'transfer' ? 'Route' : 'Name'}
              </th>
              <th className="px-4 py-2.5 text-xs font-medium tracking-wide text-zinc-500 uppercase">
                Screenshots
              </th>
              <th className="w-32 px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <ItemRow
                key={item.componentId}
                leadId={leadId}
                serial={startIndex + i + 1}
                item={item}
                shots={shotsByItem.get(item.componentId) ?? []}
                canUpload={canUpload}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export const LeadScreenshotsTab = ({
  leadId,
  canUpload,
}: {
  leadId: string;
  canUpload: boolean;
}): React.JSX.Element => {
  const { data, isLoading } = useLeadScreenshots(leadId);
  const remove = useDeleteLeadScreenshot(leadId);
  const [toDelete, setToDelete] = useState<LeadScreenshot | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-zinc-500">
        <UzSpinner size="sm" /> Loading screenshots…
      </div>
    );
  }

  const items = data?.items ?? [];
  if (!data?.itineraryId || !items.length) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-200 bg-white px-4 py-8 text-center text-sm text-zinc-500">
        {data?.itineraryId
          ? 'The converted itinerary has no bookable items.'
          : 'No converted itinerary on this lead yet. Its bookings show up here to attach screenshots to.'}
      </div>
    );
  }

  const shotsByItem = new Map<string, LeadScreenshot[]>();
  for (const s of data.screenshots) {
    if (!s.componentId) continue;
    shotsByItem.set(s.componentId, [...(shotsByItem.get(s.componentId) ?? []), s]);
  }
  const groups = KIND_ORDER.map((k) => ({
    kind: k,
    items: items.filter((i) => actionableKindOf(i.kind, i.vasKind) === k),
  })).filter((g) => g.items.length);
  // Running serial across sections, like the ops booking card.
  let running = 0;
  const withStart = groups.map((g) => {
    const start = running;
    running += g.items.length;
    return { ...g, start };
  });

  return (
    <div className="space-y-4">
      {withStart.map((g) => (
        <KindSection
          key={g.kind}
          kind={g.kind}
          items={g.items}
          startIndex={g.start}
          shotsByItem={shotsByItem}
          leadId={leadId}
          canUpload={canUpload}
          onDelete={setToDelete}
        />
      ))}

      <UzConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => {
          if (!open) setToDelete(null);
        }}
        title="Remove this screenshot?"
        description={toDelete?.fileName}
        confirmLabel="Remove"
        tone="destructive"
        onConfirm={() =>
          toDelete
            ? remove.mutateAsync(toDelete.id).then(() => setToDelete(null))
            : Promise.resolve()
        }
      />
    </div>
  );
};
